import {gql} from '@apollo/client';
import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import {DocumentNode} from 'graphql';
import PropTypes from 'prop-types';
import React from 'react';
import _ from 'underscore';

import {
  InventoryFeature,
  LabAutomationFeature,
  MolbioFeature,
  ProcessesFeature,
  RegistryFeature,
  ResultsFeature,
} from 'auth/capabilities/CapabilityFeatureConstants';
import {areAnyFeaturesEnabled, isFeatureEnabled} from 'auth/feature-enablement/FeatureStatus';
import * as PolicyDAO from 'auth/PolicyDAO';
import * as BenchlingAppDAO from 'benchling-apps/BenchlingAppDAO';
import BenchlingConfig from 'BenchlingConfig';
import * as BenchlingUtil from 'BenchlingUtil';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {User} from 'constants/UserConstants';
import * as EventsDAO from 'events/EventsDAO';
import {ErrorHandlingQuery} from 'graphql-client/BenchlingQuery';
import HomeDashboardActions from 'home-dashboard/HomeDashboardActions';
import {getTenantLevelInventorySettingsURL} from 'inventory/InventoryConstants';
import * as MonomersDAO from 'molbio/monomers/MonomersDAO';
import {areMonomersEnabledForUser} from 'molbio/monomers/MonomersUtil';
import {AllSourcesQuery} from 'nav/graphql/__generated/AllSourcesQuery';
import {ALL_SOURCES_QUERY} from 'nav/graphql/AllSources';
import {UserMenuQuery} from 'nav/navbar/__generated/UserMenuQuery';
import {ZENDESK_GUIDE_URL} from 'nav/ZendeskConstants';
import TemplateCollectionDAO from 'notebook/entry-templates/collections/TemplateCollectionDAO';
import * as ReviewLifecyclesDAO from 'notebook/reviews/review-lifecycles/ReviewLifecyclesDAO';
import {areReviewLifecyclesEnabledForUser} from 'notebook/reviews/review-lifecycles/ReviewLifecyclesUtil';
import {Organization, Team} from 'orgs/OrganizationConstants';
import OrganizationDAO from 'orgs/OrganizationDAO';
import * as TeamDAO from 'orgs/TeamDAO';
import {getPartyDisplayString} from 'parties/GetPartyDisplayString';
import * as ParameterLibraryDAO from 'procedures/parameter-library/network/LibraryParameterDefinitionDAO';
import * as ProcessSchemaDAO from 'processes/dao/ProcessSchemaDAO';
import * as RegistryUtil from 'registry/RegistryUtil';
import {getLegacyRequestObjectName} from 'requests/RequestConstants';
import {ASSAY_TYPES} from 'results/assay-schemas/AssaySchemaConstants';
import * as AssaySchemaDAO from 'results/assay-schemas/AssaySchemaDAO';
import {RESULT_PRODUCT_SCHEMAS} from 'results/ResultsNamingConstants';
import * as TestDefinitionDAO from 'sample-tests/network/TestDefinitionDAO';
import {SAMPLE_TYPES_URL} from 'sample-types/network/SampleTypeDAO';
import {schemaInterfacesURL} from 'schemas/schema-interface/SchemaInterfaceDAO';
import {schemaFieldsetNamePluralized} from 'schemas/schema-interface/util/schemaFieldsetName';
import {shouldShowDeveloperConsole} from 'sidebar/DeveloperConsoleSettings';
import UpgradeAccountModal from 'start/upgrade-account/UpgradeAccountModal';
import {getStudySchemaListingURL} from 'studies/study-schemas/StudySchemaDAO';
import {FONT_WEIGHT_PROXIMA_SEMIBOLD} from 'styles/FontConstants';
import {PADDING_SIDEPANEL} from 'styles/StyleConstants';
import {ellipsisText} from 'styles/Utilities';
import getTestProps from 'test/lib/cypress/getTestProps';
import Avatar, {NO_AVATAR_URL} from 'ui-library/avatar/Avatar';
import {AvatarSize} from 'ui-library/avatar/AvatarConstants';
import * as Colors from 'ui-library/Colors';
import LegacyDropdownMenu from 'ui-library/dropdowns/legacy/LegacyDropdownMenu';
import LegacyDropdownMenuItem from 'ui-library/dropdowns/legacy/LegacyDropdownMenuConstants';
import {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import runModal from 'ui-library/runModal';
import * as UnitDictionaryDAO from 'units/UnitDictionaryDAO';
import {capitalize} from 'util/NameUtil';
import {ElementMouseEvent} from 'util/Types';

import {NavStoreSourceGraphQL} from '../../../graphql-fragments/nav/__generated/NavStoreSourceGraphQL';
import AboutBenchlingModal, {isAboutBenchlingModalEnabled} from './AboutBenchlingModal';

type UserMenuQueryTenant = UserMenuQuery['currentTenant'];

const MAX_GROUPS = 3;

export const USER_MENU_QUERY: DocumentNode = gql`
  query UserMenuQuery @skipAudit @engComponent(name: NAVIGATION) {
    currentTenant {
      id
      avatarUrl
      permissions {
        admin
        canCreateOrganizations
        canCreateUsers
      }
    }
    currentUser {
      ... on UserNode {
        id
      }
      ... on BenchlingAppNode {
        id
      }
      ... on ServiceAccountActorNode {
        id
      }
    }
  }
`;

interface GroupOption {
  avatarUrl: string;
  handleOnClick: (e: ElementMouseEvent) => void;
  link: string;
  name: string;
}

interface SubMenu {
  icon: BenchlingIconName;
  name: string;
  dropup: boolean;
  submenu: Array<GroupOption> | Array<SubMenu> | Array<Array<GroupOption>>;
}

function getGroupMenuItems(
  groups: Array<Organization | Team>,
  icon: BenchlingIconName,
  name: string,
  getLink: (group: Organization | Team) => string
): Array<GroupOption> | Array<SubMenu> {
  const groupOptions = groups.map((group: Organization | Team) => ({
    avatarUrl: group.avatar_url,
    handleOnClick: (e: ElementMouseEvent) => {
      BenchlingUtil.navigate(e);
    },
    link: getLink(group),
    name: group.name,
    dataTestAttrs: {component: 'GroupMenu', element: 'item'},
  }));
  return groups.length > MAX_GROUPS
    ? [
        {
          icon,
          name,
          dropup: true,
          submenu: [groupOptions],
        },
      ]
    : groupOptions;
}

interface UserMenuUser extends User {
  organizations: Array<Organization>;
  teams: Array<Team>;
}

export interface UserMenuProps {
  currentUser: UserMenuUser;
  registrySettingsURL: string | null;
  currentTenant: UserMenuQueryTenant | null;
}

class PresentationalUserMenu extends React.Component<UserMenuProps, {}> {
  static propTypes = {
    currentUser: PropTypes.shape({
      avatar_url: PropTypes.string.isRequired,
      handle: PropTypes.string.isRequired,
      organizations: PropTypes.array.isRequired,
      teams: PropTypes.array.isRequired,
      url_identifier: PropTypes.string.isRequired,
      email: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
    }).isRequired,
    registrySettingsURL: PropTypes.string,
    currentTenant: PropTypes.object,
  };

  _handleClickCreateOrg = (e: ElementMouseEvent): void => {
    // If the user is ctrl-clicking, allow the browser default of opening it in a new tab.
    if (!BenchlingUtil.isSimpleClick(e)) {
      return;
    }
    e.preventDefault();
    HomeDashboardActions.openSearchOrgModal();
  };

  _renderUserItem = (): JSX.Element => (
    <li className={css(userMenuStyles.userInfo)}>
      <Avatar
        entity={this.props.currentUser}
        className={css(userMenuStyles.avatar)}
        size={AvatarSize.LARGE}
      />
      <div className={css(userMenuStyles.userText)}>
        <div
          className={css(userMenuStyles.header)}
          {...getTestProps({component: 'UserMenu', element: 'userText'})}
        >
          {getPartyDisplayString(this.props.currentUser)}
        </div>
        <div className={classNames(css(userMenuStyles.subheader), '_test-userMenu-subheader')}>
          <a
            className={css(userMenuStyles.link)}
            href={`/${this.props.currentUser.url_identifier}/settings`}
            {...getTestProps({component: 'UserMenu', element: 'settings'})}
          >
            Settings
          </a>
        </div>
      </div>
    </li>
  );

  override render(): JSX.Element {
    const organizationMenuItems = getGroupMenuItems(
      this.props.currentUser.organizations,
      ModelTypeConstants.ORGANIZATION,
      'Organizations',
      (organization: Organization) => OrganizationDAO.organizationURL(organization.url_identifier)
    );
    const teamMenuItems = getGroupMenuItems(
      this.props.currentUser.teams,
      ModelTypeConstants.TEAM,
      'Teams',
      (team: Team) => TeamDAO.teamURL(team.id)
    );
    const inviteMenuItem = {
      name: BenchlingConfig.INVITE_SCIENTISTS_EXTERNALLY ? 'Refer a peer' : 'Invite scientists',
      icon: 'invite',
      link: BenchlingConfig.INVITE_SCIENTISTS_EXTERNALLY
        ? 'https://www.benchling.com/referral-program/?utm_source=product&utm_medium=web&utm_campaign=referral_product_button'
        : '/invite',
      visible: BenchlingConfig.ENABLE_GLOBAL_INVITE_SCIENTISTS,
    };

    const currentTenant = this.props.currentTenant;
    const tenantAdminConsoleItems = [
      [
        {
          avatarUrl: currentTenant?.avatarUrl ?? NO_AVATAR_URL,
          name: 'Tenant admin console',
          handleOnClick: (e: ElementMouseEvent) => {
            BenchlingUtil.navigate(e);
          },
          link: '/admin',
          visible:
            BenchlingConfig.ENABLE_TENANT_ADMIN_CONSOLE &&
            ((currentTenant?.permissions?.admin || currentTenant?.permissions?.canCreateUsers) ?? false),
          dataTestAttrs: {component: 'TenantAdminConsole', element: 'item'},
        },
      ],
    ];

    const menuSections = [
      ...tenantAdminConsoleItems,
      organizationMenuItems,
      teamMenuItems,
      [
        {
          name: 'Feature settings',
          icon: 'settings',
          dropup: true,
          generateLinkFromSubmenu: true,
          handleOnClick: (e: ElementMouseEvent) => {
            BenchlingUtil.navigate(e);
          },
          submenu: [
            [
              {
                name: 'Registry settings',
                icon: ModelTypeConstants.REGISTRY,
                handleOnClick: (e: ElementMouseEvent) => {
                  BenchlingUtil.navigate(e);
                },
                link: this.props.registrySettingsURL,
                visible:
                  isFeatureEnabled({feature: RegistryFeature.REGISTRY_ADMIN}) &&
                  Boolean(this.props.registrySettingsURL),
              },
              {
                name: capitalize(schemaFieldsetNamePluralized()),
                icon: ModelTypeConstants.SCHEMA_INTERFACE,
                handleOnClick: (e: ElementMouseEvent) => {
                  BenchlingUtil.navigate(e);
                },
                link: schemaInterfacesURL(),
                visible: BenchlingConfig.SCHEMAS_ENABLE_INTERFACES,
              },
              {
                name: 'Inventory settings',
                icon: ModelTypeConstants.LOCATION,
                handleOnClick: (e: ElementMouseEvent) => {
                  BenchlingUtil.navigate(e);
                },
                link: getTenantLevelInventorySettingsURL(),
                visible: isFeatureEnabled({feature: InventoryFeature.INVENTORY_ADMIN}),
              },
              {
                name: 'Monomer library',
                icon: 'monomer',
                handleOnClick: (e: ElementMouseEvent) => {
                  BenchlingUtil.navigate(e);
                },
                link: MonomersDAO.MONOMER_LIBRARY_URL,
                visible: areMonomersEnabledForUser(),
              },
              {
                name: 'Unit dictionary',
                icon: 'unit',
                handleOnClick: (e: ElementMouseEvent) => {
                  BenchlingUtil.navigate(e);
                },
                link: UnitDictionaryDAO.UNIT_DICTIONARY_URL,
                visible: true,
              },
              {
                name: 'Parameter library',
                icon: 'parameters',
                handleOnClick: (e: ElementMouseEvent) => {
                  BenchlingUtil.navigate(e);
                },
                link: ParameterLibraryDAO.libraryParameterDefinitionsListingURL(),
                visible: BenchlingConfig.PROCEDURES_ENABLE_PARAMETER_LIBRARY,
              },
              {
                name: 'Template collections',
                icon: ModelTypeConstants.TEMPLATE_COLLECTION,
                handleOnClick: (e: ElementMouseEvent) => {
                  BenchlingUtil.navigate(e);
                },
                link: TemplateCollectionDAO.collectionsURL(),
              },
              {
                name: 'Review processes',
                icon: 'review_lifecycles',
                handleOnClick: (e: ElementMouseEvent) => {
                  BenchlingUtil.navigate(e);
                },
                link: ReviewLifecyclesDAO.reviewLifecyclesListingURL(),
                visible: areReviewLifecyclesEnabledForUser(),
              },
              {
                name: 'Run schemas',
                icon: ModelTypeConstants.RUN_SCHEMA,
                handleOnClick: (e: ElementMouseEvent) => {
                  BenchlingUtil.navigate(e);
                },
                link: AssaySchemaDAO.assaySchemasURL(ASSAY_TYPES.ASSAY_RUN),
                visible: areAnyFeaturesEnabled([{feature: LabAutomationFeature.LAB_AUTOMATION_ADMIN}]),
              },
              {
                name: RESULT_PRODUCT_SCHEMAS,
                icon: ModelTypeConstants.RESULT_SCHEMA,
                handleOnClick: (e: ElementMouseEvent) => {
                  BenchlingUtil.navigate(e);
                },
                link: AssaySchemaDAO.assaySchemasURL(ASSAY_TYPES.ASSAY_RESULT),
                visible: isFeatureEnabled({feature: ResultsFeature.RESULTS_ADMIN}),
              },
              {
                name: `${getLegacyRequestObjectName()} schemas`,
                icon: ModelTypeConstants.REQUEST_SCHEMA,
                handleOnClick: (e: ElementMouseEvent) => {
                  BenchlingUtil.navigate(e);
                },
                link: AssaySchemaDAO.assaySchemasURL(ASSAY_TYPES.ASSAY_REQUEST),
                visible: BenchlingConfig.ENABLE_REQUESTS,
              },
              {
                name: 'Study schemas',
                icon: ModelTypeConstants.STUDY,
                handleOnClick: (e: ElementMouseEvent) => {
                  BenchlingUtil.navigate(e);
                },
                link: getStudySchemaListingURL(),
                visible: BenchlingConfig.STUDIES_ENABLE,
              },
              {
                name: 'Sample types',
                icon: 'sample',
                handleOnClick: (e: ElementMouseEvent) => {
                  BenchlingUtil.navigate(e);
                },
                link: SAMPLE_TYPES_URL,
                visible: BenchlingConfig.SAMPLE_TYPES_ENABLE,
              },
              {
                name: 'Workflow schemas',
                icon: 'process',
                handleOnClick: (e: ElementMouseEvent) => {
                  BenchlingUtil.navigate(e);
                },
                link: ProcessSchemaDAO.processTaskSchemasURL(),
                visible: isFeatureEnabled({feature: ProcessesFeature.PROCESSES_ADMIN}),
              },
              {
                name: 'Test definitions',
                icon: 'protocol-table',
                handleOnClick: (e: ElementMouseEvent) => {
                  BenchlingUtil.navigate(e);
                },
                link: TestDefinitionDAO.testDefinitionsListingURL(),
                visible: BenchlingConfig.SAMPLE_TESTS_ENABLE,
              },
              {
                name: 'Access policies',
                icon: 'lock',
                handleOnClick: (e: ElementMouseEvent) => {
                  BenchlingUtil.navigate(e);
                },
                link: PolicyDAO.getDataPoliciesURL(),
                visible: BenchlingConfig.ENABLE_POLICY_UI,
              },
              {
                name: 'Developer console',
                icon: 'console',
                handleOnClick: (e: ElementMouseEvent) => {
                  BenchlingUtil.navigate(e);
                },
                link: BenchlingConfig.ENABLE_BENCHLING_APPS
                  ? BenchlingAppDAO.BENCHLING_APPS_WEB_URL
                  : EventsDAO.EVENT_SUBSCRIPTIONS_URL,
                visible: shouldShowDeveloperConsole(currentTenant?.permissions?.admin ?? false),
              },
            ],
          ],
          dataTestAttrs: {component: 'FeatureSettings', element: 'item'},
        },
        {
          name: 'Molecular biology settings',
          icon: 'wrench',
          dropup: true,
          generateLinkFromSubmenu: true,
          submenu: [
            [
              {
                name: 'Enzyme lists',
                icon: 'enzyme',
                link: `/${this.props.currentUser.url_identifier}/enzyme-lists`,
                visible: isFeatureEnabled({feature: MolbioFeature.MOLBIO_ADMIN}),
              },
              {
                name: 'Feature libraries',
                icon: 'feature-libraries',
                link: `/${this.props.currentUser.url_identifier}/feature-libraries`,
                visible: isFeatureEnabled({feature: MolbioFeature.MOLBIO_ADMIN}),
              },
              {
                name: 'Ladders',
                icon: 'ladder',
                link: `/${this.props.currentUser.url_identifier}/ladders`,
                visible: isFeatureEnabled({feature: MolbioFeature.MOLBIO_ADMIN}),
              },
            ],
          ],
        },
        {
          name: 'Data export',
          icon: 'export',
          extraClasses: 'js-create-new-export',
          visible: BenchlingConfig.ENABLE_EXPORTS,
        },
      ],
      [
        {
          name: 'Help',
          icon: 'more-info',
          link: BenchlingConfig.ENABLE_ZENDESK_GUIDE ? ZENDESK_GUIDE_URL : BenchlingConfig.SUPPORT_SITE_URL,
          submenu: [
            _.compact([
              isAboutBenchlingModalEnabled() && {
                name: 'About Benchling',
                icon: 'benchling',
                handleOnClick: () => {
                  runModal(AboutBenchlingModal, {});
                },
              },
              {
                name: 'Help center',
                icon: 'more-info',
                link: BenchlingConfig.ENABLE_ZENDESK_GUIDE
                  ? ZENDESK_GUIDE_URL
                  : BenchlingConfig.SUPPORT_SITE_URL,
              },
              {
                name: 'Ask the community',
                icon: 'team',
                link: 'https://community.benchling.com/',
              },
            ]),
          ],
        },
        inviteMenuItem,
      ],
      [
        {
          name: 'Upgrade account',
          icon: 'star',
          handleOnClick: () => {
            // @ts-expect-error ts-migrate: need to add initialTab here, or make optional
            UpgradeAccountModal.open();
          },
          visible: UpgradeAccountModal.isEnabled(this.props.currentUser),
          extraIconClasses: 'navDropdownMenu-item-icon--green',
        },
        {
          name: 'Create or join organization',
          handleOnClick: this._handleClickCreateOrg,
          icon: 'compose',
          extraClasses: 'legacy-font-proxima-title js-createOrganizationButton',
          link: '/organizations/create',
          extraIconClasses: 'navDropdownMenu-item-icon--blue',
          visible:
            !BenchlingConfig.PRIVATE_DEPLOY || (currentTenant?.permissions?.canCreateOrganizations ?? false),
        },
        {
          name: 'Sign out',
          icon: 'sign-out',
          extraClasses: 'legacy-font-proxima-title',
          link: '/signout',
          dataMethod: 'POST',
          extraIconClasses: 'navDropdownMenu-item-icon--red',
        },
      ],
    ];

    // The user section is wrapped in a form tag to prevent clicking from dismissing the menu.
    const userItem = <form className={css(userMenuStyles.container)}>{this._renderUserItem()}</form>;
    return (
      <LegacyDropdownMenu
        className="dropdown-menu--dropRightUp"
        extraElement={userItem}
        items={menuSections as Array<Array<LegacyDropdownMenuItem>>}
        {...getTestProps({component: 'UserMenu', element: 'dropdownMenu'})}
      />
    );
  }
}

function getRegistrySettingsURL(sources: Array<NavStoreSourceGraphQL>): string | null {
  const registry = sources.flatMap((source) => (source.__typename === 'RegistryNode' ? [source] : []))[0];
  return registry
    ? RegistryUtil.getRegistrySettingsUrl(registry.ownerJson.url_identifier, registry.id)
    : null;
}

export default function UserMenu(
  props: Omit<UserMenuProps, 'registrySettingsURL' | 'currentTenant'>
): JSX.Element {
  // TODO(GraphQL): This will be cleaner once we upgrade to Apollo Client 3 and get hooks. (As of writing,
  // they're still in beta).
  const innerProps = {...props, registrySettingsURL: null, currentTenant: null};
  return (
    <ErrorHandlingQuery<UserMenuQuery> query={USER_MENU_QUERY}>
      {({data: userMenuData, loading: userMenuQueryLoading}) => (
        <ErrorHandlingQuery<AllSourcesQuery> query={ALL_SOURCES_QUERY}>
          {({data: sourcesData, loading: sourcesLoading}) => (
            <PresentationalUserMenu
              {...innerProps}
              currentTenant={userMenuData?.currentTenant ?? null}
              registrySettingsURL={sourcesLoading ? null : getRegistrySettingsURL(sourcesData!.sources)}
            />
          )}
        </ErrorHandlingQuery>
      )}
    </ErrorHandlingQuery>
  );
}

const linkHoverStyles = {
  color: Colors.GRAY_LIGHT,
  textDecoration: 'none',
};
export const userMenuStyles = StyleSheet.create({
  container: {
    borderRadius: 'inherit',
  },
  userText: {
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-evenly',
  },
  userInfo: {
    backgroundColor: Colors.BLUE_DARKER,
    borderRadius: 'inherit',
    color: Colors.WHITE,
    cursor: 'default',
    display: 'flex',
    padding: PADDING_SIDEPANEL,
  },
  header: {
    ...ellipsisText,
    fontSize: 16,
    lineHeight: '24px',
    marginBottom: 6,
  },
  subheader: {
    fontSize: 12,
    lineHeight: '16px',
    marginBottom: 3,
  },
  link: {
    color: Colors.WHITE,
    fontWeight: FONT_WEIGHT_PROXIMA_SEMIBOLD,
    ':hover': linkHoverStyles,
    ':focus': linkHoverStyles,
    ':active': {
      color: Colors.GRAY,
      textDecoration: 'none',
    },
  },
  avatar: {
    marginRight: 14,
  },
});
